import { createApiRef } from '@backstage/core-plugin-api';
import type {
  AlertLogLevel,
  RequestState,
  WorkflowType,
} from '@internal/backstage-plugin-vault-common';

// AlertLogLevel, RequestState (re-exported here as VaultRequestState — the
// name every consumer in this plugin already imports), and WorkflowType all
// moved to @internal/backstage-plugin-vault-common (GH #1013) — the single
// source of truth shared with vault-backend, which previously hand-copied
// all three with no shared source. RequestState had already drifted once as
// a result: this file's own copy needed 'deploy_interrupted' backfilled
// after it was added to vault-backend's union and missed here.
export type { AlertLogLevel };

export interface EngineConfigResponse {
  source: 'live' | 'manifest';
  targetCluster: string;
  machinePolicy: { name: string; path: string; capabilities: string[] } | null;
  humanAccess: {
    policy: { name: string; path: string; capabilities: string[] };
    ldapGroupDn: string;
    vaultLdapAuthPath?: string;
    users: string[];
  } | null;
  engines: {
    pki?: {
      allowedDomains: string[];
      maxTtl: string;
      roleName?: string;
      path?: string;
      policyName?: string;
    };
    database?: {
      backend: string;
      rotationTtl: string;
      roleName?: string;
      policyName?: string;
    };
    awsIam?: {
      iamRole: string;
      credentialTtl: string;
      roleName?: string;
      policyName?: string;
    };
    sshCa?: {
      certType: string;
      maxTtl: string;
      defaultTtl: string;
      allowedUsers?: string[];
      defaultUser?: string;
      allowedHosts?: string[];
      allowedExtensions?: string[];
      roleName?: string;
      policyName?: string;
    };
  };
  kvAccess: {
    delegatedAccess: Array<{
      subpath: string;
      group: string;
      capabilities: string[];
      policyName?: string;
      path?: string;
    }>;
  } | null;
}

// Sourced from @internal/backstage-plugin-vault-common's RequestState (see
// the module-header comment above) — aliased to the name every consumer in
// this plugin already imports.
export type VaultRequestState = RequestState;

// One approval milestone from the request's SNOW/approval progression. Mirrors
// vault-backend's ApprovalMilestone — the backend already emits `approvalTrail`
// on the request record; this frontend type declares it so cards can render the
// ownership approval chain (Manager → Tech group → Tech lead → final decision).
export interface ApprovalMilestone {
  stage: number;
  roleLabel: string;
  status: 'pending' | 'approved';
  approver: string | null;
  timestamp: string | null;
}

export interface VaultRequestRecord {
  requestId: string;
  state: VaultRequestState;
  // Gates the "Change Datacenters" edit affordance to Workflow A only
  // (ClickUp 86e21uzu5).
  workflowType?: WorkflowType;
  bsn: string;
  // Scalar in practice today (one manifest per environment); array is
  // forward-compatible with future multi-environment support. See
  // @internal/backstage-plugin-vault-backend's VaultManifest/VaultRequest.
  environment: string | string[];
  applicationType: string;
  accessType: string;
  requester: string;
  chgNumber: string | null;
  ritmNumber: string | null;
  approver: string | null;
  ticketUrl: string | null;
  prUrl: string | null;
  manifest: string;
  generatedAt: string;
  approvedAt: string | null;
  // Timestamp the request reached `deployed` (#503). null until deployed.
  deployedAt: string | null;
  // Reason captured when a request was rejected (#504). null unless rejected.
  rejectionReason: string | null;
  // SNOW Access Request id, alongside chg/ritm (#502). null when no AR.
  arNumber: string | null;
  reviewDate: string;
  updatedAt: string;
  // Emitted by vault-backend today but previously undeclared on the frontend:
  // the AWX/AAP deployment job id (#599) and the ordered approval trail (#600).
  awxJobId: string | null;
  approvalTrail: ApprovalMilestone[];
  // Latest stall reason/service, present only when state is one of
  // stuck/snow_stalled/gitops_stalled/deploy_failed — derived server-side
  // from the request's most recent stall-tagged event. Drives the
  // dashboard's hover-only "Admin Action Required" tooltip.
  stallReason?: 'unavailable' | 'rejected' | 'job_failed' | null;
  stallService?: 'servicenow' | 'gitops' | 'opa' | 'awx' | null;
  // Current target cluster(s), parsed server-side from the stored manifest
  // (ClickUp 86e21uzu5) — surfaces what "Change Datacenters" would relocate.
  targetClusters?: string[] | null;
  // Edit lifecycle — points at the request this one edits, if any. Absent
  // on every ordinary onboarding/rotation/namespace request.
  parentRequestId?: string | null;
}

export interface VaultRequestEvent {
  id: string;
  requestId: string;
  fromState: VaultRequestState | null;
  toState: VaultRequestState;
  actor: string;
  note: string | null;
  occurredAt: string;
  chgNumber?: string | null;
  stallReason?: 'unavailable' | 'rejected' | 'job_failed' | null;
  stallService?: 'servicenow' | 'gitops' | 'opa' | 'awx' | null;
}

export interface VaultApi {
  getRequest(
    requestId: string,
  ): Promise<{ request: VaultRequestRecord; events: VaultRequestEvent[] }>;
  listUserRequests(requester: string): Promise<VaultRequestRecord[]>;
  listAllRequests(adminEmail: string): Promise<VaultRequestRecord[]>;
  // Every request ever filed for this BSN, current and past — the source for
  // the profile's Change History tab (RITM stays constant across a BSN's
  // lifecycle; each request gets its own CHG).
  listRequestsByBsn(bsn: string): Promise<VaultRequestRecord[]>;
  abandonRequest(
    requestId: string,
    adminEmail: string,
    note: string,
  ): Promise<void>;
  checkAdmin(email: string): Promise<{ isAdmin: boolean }>;
  getAlertLogLevel(email: string): Promise<{ logLevel: AlertLogLevel }>;
  setAlertLogLevel(email: string, logLevel: AlertLogLevel): Promise<void>;
  cancelRequest(
    requestId: string,
    requester: string,
  ): Promise<{
    teardownStatus: 'ok' | 'skipped' | 'auth_failed' | 'partial';
    skippedPaths: string[];
  }>;
  retryCleanup(requestId: string, requester: string): Promise<void>;
  retryRequest(requestId: string, adminEmail: string): Promise<void>;
  checkOpaHealth(): Promise<{ reachable: boolean }>;
  retryAll(adminEmail: string): Promise<{ queued: number; skipped: number }>;
  getEngineConfig(requestId: string): Promise<EngineConfigResponse>;
  initiateDecommission(
    requestId: string,
    reason: string,
    justification: string,
    requester: string,
  ): Promise<void>;
  // Edit lifecycle (ClickUp 86e21uzu5) — "Change Datacenters." Creates a new
  // linked edit request for the given deployed Workflow A request. Returns
  // the new edit request's id, which callers navigate to.
  editDatacenters(
    requestId: string,
    targetClusters: string[],
    requester: string,
  ): Promise<{ requestId: string }>;
  // Engine/KV config edit (GH #842, superseding #841) — creates a new
  // linked edit request, same governed pipeline as editDatacenters. kvPolicy
  // is always required (every deployed BSN has one); an engine key omitted
  // means it was turned off in that engine's edit dialog.
  requestEngineUpdate(
    requestId: string,
    updates: EngineConfigUpdatePayload,
    requester: string,
  ): Promise<{ requestId: string }>;
  // "Add Environment" (ClickUp 86e21uzu5, second half) — clones the given
  // deployed Workflow A request's config into a brand-new request for
  // `environment` (a genuinely new environment for the same BSN, not an
  // edit of this request). Returns the new request's id, which callers
  // navigate to.
  addEnvironment(
    requestId: string,
    environment: string,
    requester: string,
  ): Promise<{ requestId: string }>;
}

export interface EngineConfigUpdatePayload {
  kvPolicy: { path?: string; capabilities: string[] };
  pki?: {
    roleName?: string;
    allowedDomains: string[];
    maxTtl: string;
    // Matches onboarding's actual PKI fields (VaultOnboardingForm.tsx) —
    // a key-usage checkbox-group value plus free-text extended key usage —
    // rather than the previous simplified serverAuth/clientAuth dropdown.
    keyUsage?: string[];
    extKeyUsage?: string;
  };
  database?: {
    backend: string;
    roleName?: string;
    rotationTtl: string;
  };
  awsIam?: {
    iamRole: string;
    credentialTtl: string;
  };
  sshCa?: {
    roleName?: string;
    certType: 'user' | 'host' | 'both';
    maxTtl: string;
    defaultTtl?: string;
    allowedUsers?: string[];
    defaultUser?: string;
    allowedHosts?: string[];
    allowedExtensions?: string[];
  };
}

export const vaultApiRef = createApiRef<VaultApi>({
  id: 'plugin.vault-requests.api',
});
